// Keep the identical catalog/helpers in next, node, and mobile in sync.
// Source: requested diagnosis-code table (September 2026). Parents remain
// readable for existing records but are never offered as selectable diagnoses.
export const DIAGNOSIS_FAMILIES = [
  {
    code: 'N18.3',
    name: 'Moderate Kidney Disease, Stage 3',
    children: [
      {
        code: 'N18.30',
        name: 'Chronic kidney disease, stage 3 unspecified',
      },
      {
        code: 'N18.31',
        name: 'Chronic kidney disease, stage 3a',
      },
      {
        code: 'N18.32',
        name: 'Chronic kidney disease, stage 3b',
      },
    ],
  },
  {
    code: 'F50.01',
    name: 'Anorexia, Restricting Type',
    children: [
      {
        code: 'F50.010',
        name: 'Anorexia nervosa, restricting type, mild',
      },
      {
        code: 'F50.011',
        name: 'Anorexia nervosa, restricting type, moderate',
      },
      {
        code: 'F50.012',
        name: 'Anorexia nervosa, restricting type, severe',
      },
      {
        code: 'F50.013',
        name: 'Anorexia nervosa, restricting type, extreme',
      },
      {
        code: 'F50.014',
        name: 'Anorexia nervosa, restricting type, in remission',
      },
      {
        code: 'F50.019',
        name: 'Anorexia nervosa, restricting type, unspecified',
      },
    ],
  },
  {
    code: 'F50.02',
    name: 'Anorexia, Binge-Eating/Purging Type',
    children: [
      {
        code: 'F50.020',
        name: 'Anorexia nervosa, binge eating/purging type, mild',
      },
      {
        code: 'F50.021',
        name: 'Anorexia nervosa, binge eating/purging type, moderate',
      },
      {
        code: 'F50.022',
        name: 'Anorexia nervosa, binge eating/purging type, severe',
      },
      {
        code: 'F50.023',
        name: 'Anorexia nervosa, binge eating/purging type, extreme',
      },
      {
        code: 'F50.024',
        name: 'Anorexia nervosa, binge eating/purging type, in remission',
      },
      {
        code: 'F50.029',
        name: 'Anorexia nervosa, binge eating/purging type, unspecified',
      },
    ],
  },
  {
    code: 'F50.2',
    name: 'Bulimia',
    children: [
      {
        code: 'F50.20',
        name: 'Bulimia nervosa, unspecified',
      },
      {
        code: 'F50.21',
        name: 'Bulimia nervosa, mild',
      },
      {
        code: 'F50.22',
        name: 'Bulimia nervosa, moderate',
      },
      {
        code: 'F50.23',
        name: 'Bulimia nervosa, severe',
      },
      {
        code: 'F50.24',
        name: 'Bulimia nervosa, extreme',
      },
      {
        code: 'F50.25',
        name: 'Bulimia nervosa, in remission',
      },
    ],
  },
  {
    code: 'Z91.011',
    name: 'Dairy Allergy',
    children: [
      {
        code: 'Z91.0110',
        name: 'Allergy to milk products, unspecified',
      },
      {
        code: 'Z91.0111',
        name: 'Allergy to milk products with tolerance to baked milk',
      },
      {
        code: 'Z91.0112',
        name: 'Allergy to milk products with reactivity to baked milk',
      },
    ],
  },
  {
    code: 'Z91.012',
    name: 'Egg Allergy',
    children: [
      {
        code: 'Z91.0120',
        name: 'Allergy to eggs, unspecified',
      },
      {
        code: 'Z91.0121',
        name: 'Allergy to eggs with tolerance to baked egg',
      },
      {
        code: 'Z91.0122',
        name: 'Allergy to eggs with reactivity to baked egg',
      },
    ],
  },
]

/** Find a nonselectable parent group by its exact code. */
export const getDiagnosisFamily = (code: string) =>
  DIAGNOSIS_FAMILIES.find(family => family.code === code)

/** Return the parent code for a selectable child. */
export const getDiagnosisParent = (code: string) =>
  DIAGNOSIS_FAMILIES.find(family =>
    family.children.some(child => child.code === code)
  )?.code

/** Identify legacy parents that still need a specific child selection. */
export const unresolvedDiagnosisCodes = (codes: string[]) =>
  codes.filter(code => getDiagnosisFamily(code))

/** Remove a parent only when a child from that exact family was selected. */
export const replaceSelectedParents = <T>(
  items: T[],
  codeOf: (item: T) => string
): T[] => {
  const replaced = new Set(
    items.map(item => getDiagnosisParent(codeOf(item))).filter(Boolean)
  )
  return items.filter(item => !replaced.has(codeOf(item)))
}

/** Expand legacy parent options into labeled groups of selectable children. */
export const expandDiagnosisGroups = <
  T extends { value: string; label: string }
>(
  groups: { label: string; options: T[] }[]
): { label: string; options: T[] }[] =>
  groups.flatMap(group => {
    const result: { label: string; options: T[] }[] = []
    let pending: T[] = []
    const flush = () => {
      if (pending.length) result.push({ label: group.label, options: pending })
      pending = []
    }
    for (const option of group.options) {
      const family = getDiagnosisFamily(option.value)
      if (family) {
        flush()
        result.push({
          label: `${group.label} — ${family.name}`,
          options: family.children.map(child => ({
            ...option,
            value: child.code,
            label: child.name,
          })),
        })
      } else pending.push(option)
    }
    flush()
    return result
  })
